<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import LogRocket from 'logrocket';
import { useTemplateRefsList } from '@vueuse/core';

import { useCalcApi } from '@/helpers/useCalcApi';
import { useProductType } from '@/helpers/useProductType';

import PrivacyConsentModal from '@/components/shared/PrivacyConsentModal.vue';
import { createApplicant } from '@/helpers/builder';
import { ApplicantType, AppValues } from '@/helpers/const';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { toast } from '@/helpers/toast';
import {
  useRelationshipManager,
  transformApiToUiFormat,
  transformRelationshipStatuses,
  prepareFinancials,
} from '@/helpers/useRelationshipManager';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';

import ApplicantCard from './modules/ApplicantDetails/ApplicantCard.vue';
import ScenarioTypeTabs from './modules/ScenarioTypeTabs.vue';
import BorrowerTypeSelector from './modules/BorrowerDetails/BorrowerTypeSelector.vue';

import type { ApplicantEntry, LoanApplication } from '@/types';
import type { BorrowerTypeSelectionEnum } from '@/helpers/const';
import type { ProductTypeBorrowerType } from '@/api/backend/public/product-type';

const { loanApp, saveAndGoNext, goBack } = useUserQuoteProcess();
const { getMaxSecondaryApplicants } = useFeatureFlags();

const borrowerTypeSelection = ref<BorrowerTypeSelectionEnum | undefined>(loanApp.value.borrowerTypeSelection);

const calc = useCalcApi();

const { isCommercialProduct } = useProductType(loanApp);

watch(
  () => loanApp.value.borrowerTypeSelection,
  (val) => {
    borrowerTypeSelection.value = val;
  },
  { immediate: true },
);

const showPrivacyModal = ref(false);

const initializeApplicants = (): ApplicantEntry[] => {
  if (loanApp.value.applicants.length > 0) {
    return transformApiToUiFormat(loanApp.value.applicants);
  }
  return [createApplicant(ApplicantType.PRIMARY)];
};

const initialApplicants = initializeApplicants();
const primary = ref<ApplicantEntry>(
  initialApplicants.find((a) => a.type === ApplicantType.PRIMARY) ?? createApplicant(ApplicantType.PRIMARY),
);
const secondaries = ref<ApplicantEntry[]>(initialApplicants.filter((a) => a.type === ApplicantType.SECONDARY));

const primaryRef = ref<InstanceType<typeof ApplicantCard>>();
const secondaryRefs = useTemplateRefsList<InstanceType<typeof ApplicantCard>>();

const allowAdditionalApplicants = computed(() => secondaries.value.length < getMaxSecondaryApplicants());
const allApplicants = computed(() => [primary.value, ...secondaries.value]);
const { validate: relationshipValidate, handleRelationshipChange: relationshipManagerHandleChange } =
  useRelationshipManager(allApplicants);

const handleRelationshipChange = (applicantId: string, newStatus: string, oldStatus?: string) => {
  relationshipManagerHandleChange(applicantId, newStatus, oldStatus, primary, secondaries);
};

const validateRelations = () => {
  const relErrors = relationshipValidate.value;
  if (relErrors.length) return false;
  return true;
};

const validateApplicants = async () => {
  const validatePrimary = (await primaryRef.value?.update()) ?? false;
  const renderedRefs = secondaryRefs.value.filter((ref) => ref?.update);
  const validateSecondaries = await Promise.all(renderedRefs.map((ref) => ref.update()));
  const allSecondariesValid = validateSecondaries.every(Boolean);
  return validatePrimary && allSecondariesValid;
};

const addApplicant = async () => {
  if (!allowAdditionalApplicants.value) return;
  const primaryValid = (await primaryRef.value?.validateForAdd()) ?? false;
  const renderedRefs = secondaryRefs.value.filter((ref) => ref?.validateForAdd);
  const secondaryResults = await Promise.all(renderedRefs.map((ref) => ref.validateForAdd()));
  if (primaryValid && secondaryResults.every(Boolean)) {
    secondaries.value.push(createApplicant(ApplicantType.SECONDARY));
  }
};

const removeApplicant = (applicantId: string) => {
  secondaries.value = secondaries.value.filter((a) => a.id !== applicantId);
};

const saveAndContinue = async () => {
  if (!borrowerTypeSelection.value) {
    toast({
      type: 'danger',
      title: 'Error',
      description: 'Please select borrower type.',
    });
    return;
  }
  if (!(await validateApplicants())) return;
  if (!validateRelations()) return;

  const applicants = transformRelationshipStatuses(allApplicants.value);
  const financials = prepareFinancials(allApplicants.value, loanApp.value.financials);
  const borrowerType = borrowerTypeSelection.value as unknown as ProductTypeBorrowerType;
  const productTypePatch: Partial<Pick<LoanApplication, 'productType' | 'assessmentType'>> = {};

  try {
    const matrixResult = await calc.getProductType(loanApp.value, {
      ...(borrowerType ? { borrowerType: borrowerType } : {}),
    });

    if (matrixResult?.loanType) {
      productTypePatch.productType = matrixResult.loanType;
      productTypePatch.assessmentType = matrixResult.assessmentType;
    }
  } catch (error) {
    console.error('Failed to determine product type after borrower details:', error);
  }

  const domainUrl = import.meta.env.VITE_APP_CUSTOM_THEME_DOMAIN_URL ?? 'apply.bridgit.com.au';
  if (window.location.host === domainUrl && primary.value.email?.length) {
    LogRocket.identify(primary.value.email, {
      name: `${primary.value.firstName} ${primary.value.lastName}`,
      email: primary.value.email as string,
      mobilePhoneNumber: primary.value.mobile as string,
    });
  }

  saveAndGoNext({
    applicants,
    financials,
    borrowerTypeSelection: borrowerTypeSelection.value,
    ...productTypePatch,
  });
};
</script>

<template>
  <div class="mb-4 pb-2">
    <h2 class="text-2xl font-medium">
      Tell us about the
      <BxHighlight>borrowers</BxHighlight>
    </h2>
    <p v-if="allowAdditionalApplicants" class="mt-2">
      You can add a second borrower by clicking 'add another borrower' below.
    </p>
  </div>
  <div class="section-panel md:p-6">
    <div class="mb-6">
      <ScenarioTypeTabs :section-key="'applicantDetails'" title="Borrower details" />
    </div>
    <div class="mt-6">
      <BorrowerTypeSelector
        v-model="borrowerTypeSelection"
        :is-commercial="isCommercialProduct"
        :required="true"
        class="mt-6"
      />
    </div>
    <div class="mt-6">
      <ApplicantCard
        ref="primaryRef"
        v-model="primary"
        type="primary"
        :all-applicants="allApplicants"
        :current-id="primary.id"
        @relationship-change="handleRelationshipChange"
      />
    </div>
    <div v-for="(secondary, index) in secondaries" :key="secondary.id || index" class="mt-6">
      <ApplicantCard
        :ref="secondaryRefs.set"
        v-model="secondaries[index]"
        type="secondary"
        :primary="primary"
        :all-applicants="allApplicants"
        :current-id="secondary.id"
        @remove="removeApplicant"
        @relationship-change="handleRelationshipChange"
      />
    </div>
    <div v-if="allowAdditionalApplicants" class="mt-6">
      <BxButton id="applicants-add" data-test-id="applicants-add-btn" variant="tertiary" @click="addApplicant()">
        <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
        Add another borrower
      </BxButton>
    </div>
    <p v-if="loanApp.userType !== 'Broker' && loanApp.userType !== 'Crm'" class="mt-10 text-sm">
      By clicking <strong>"Save and continue"</strong> you confirm that you have read and agree to
      {{ AppValues.COMPANY_NAME + `'s` }}
      <a class="link" data-test-id="applicants-privacy-consent-link" @click="showPrivacyModal = true">
        Privacy Consent and Electronic Authorisation</a
      >
      and consent to the collection, use, holding and disclosure of my/our personal information as set out in that
      document, and to receiving notices, statements, disclosures and other documents from
      {{ AppValues.COMPANY_NAME }} electronically.
    </p>
    <div class="mt-10">
      <BxButton
        id="applicants-continue"
        data-test-id="applicants-continue-btn"
        variant="primary"
        @click="saveAndContinue()"
      >
        Save and continue
      </BxButton>
      <BxButton data-test-id="applicants-back-btn" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
    <PrivacyConsentModal v-model="showPrivacyModal" />
  </div>
</template>

<style lang="scss" scoped>
.link {
  @apply cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen;
}
</style>
